import { useEffect, useMemo, useState } from "react";
import { Button } from "@oc-labs/wt-storybook";
import { Plus, Search, FileCode, Rocket, Layers } from "lucide-react";
import toast from "react-hot-toast";
import UpsertSourceCodeModal from "../../../components/modals/source-code/UpsertSourceCodeModal";
import DeploySourceModal from "../../../components/modals/source-code/DeploySourceModal";
import SourceDeploymentsModal from "../../../components/modals/source-code/SourceDeploymentsModal";
import DeleteModal from "../../../components/modals/DeleteModal";
import useModal from "../../../hooks/useModal";
import useSourceCode from "../../../hooks/useSourceCode";

const formatBytes = (bytes) => {
  if (bytes == null || isNaN(bytes)) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString();
};

export default function AdminSourceCode() {
  const { adminSourceCodes, adminDeleteSourceCode } = useSourceCode();
  const { open: openUpsertModal } = useModal("upsert-source-code-modal");
  const { open: openDeployModal } = useModal("deploy-source-modal");
  const { open: openDeploymentsModal } = useModal("source-deployments-modal");
  const { open: openDeleteModal } = useModal("delete-modal");
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, []);

  const load = () => {
    setLoading(true);
    adminSourceCodes()
      .then((res) => setItems(Array.isArray(res) ? res : []))
      .catch((err) => toast.error(err?.response?.data?.message || err.message || "Failed to load source codes"))
      .finally(() => setLoading(false));
  };

  const handleDelete = (item) => {
    openDeleteModal({
      title: "Delete Source Code",
      text: `Delete "${item.name}"? This action cannot be undone.`,
      onDelete: async () => {
        try {
          await adminDeleteSourceCode(item.name);
          toast.success("Source code deleted");
          load();
        } catch (err) {
          toast.error(err?.response?.data?.message || err.message || "Failed to delete");
        }
      },
    });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => it.name?.toLowerCase().includes(q));
  }, [items, search]);

  return (
    <>
      <UpsertSourceCodeModal onUpsert={load} />
      <DeploySourceModal onDeployed={load} />
      <SourceDeploymentsModal />
      <DeleteModal />
      <div className="2xl:p-6 p-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <FileCode className="text-blue-600" />
            <h1 className="2xl:text-2xl text-lg font-bold text-gray-900">Source Code</h1>
            <span className="text-sm text-gray-500 ml-2">{items.length} files</span>
          </div>
          <div>
            <Button
              type="secondary"
              size="small"
              onClick={() => openUpsertModal({ __isNew: true })}
              icon={<Plus size={16} />}
              className="whitespace-nowrap"
            >
              New Source Code
            </Button>
          </div>
        </div>

        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name"
              className="form-input w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border border-gray-300 bg-white h-10 pl-9 pr-4 text-sm"
            />
          </div>
        </div>

        <div className="rounded-lg shadow overflow-hidden">
          <table className="w-full bg-white table-fixed">
            <thead className="bg-gray-100 uppercase">
              <tr>
                <th className="2xl:py-3 py-2 2xl:px-6 px-3 text-left text-xs font-medium tracking-wider w-[35%]">Name</th>
                <th className="2xl:py-3 py-2 2xl:px-6 px-3 text-left text-xs font-medium tracking-wider w-[10%]">Size</th>
                <th className="2xl:py-3 py-2 2xl:px-6 px-3 text-left text-xs font-medium tracking-wider w-[15%]">Last modified</th>
                <th className="2xl:py-3 py-2 2xl:px-6 px-3 text-left text-xs font-medium tracking-wider w-[15%]">Deployments</th>
                <th className="2xl:py-3 py-2 2xl:px-6 px-3 text-left text-xs font-medium tracking-wider w-[25%]"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading && (
                <tr>
                  <td className="py-8 px-6 text-center text-gray-500" colSpan={5}>
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td className="py-8 px-6 text-center text-gray-500" colSpan={5}>
                    No source code files found.
                  </td>
                </tr>
              )}
              {!loading && filtered.map((item) => {
                const deployments = Array.isArray(item.deployments) ? item.deployments : [];
                const deployCount = deployments.length;
                return (
                  <tr key={item.name} className="hover:bg-gray-50 transition">
                    <td className="py-4 px-6 text-sm font-medium text-gray-900 font-mono truncate">{item.name}</td>
                    <td className="py-4 px-6 text-sm text-gray-700 truncate">{formatBytes(item.size)}</td>
                    <td className="py-4 px-6 text-sm text-gray-500 truncate">{formatDate(item.lastModified)}</td>
                    <td className="py-4 px-6 text-sm">
                      {deployCount > 0 ? (
                        <button
                          type="button"
                          onClick={() =>
                            openDeploymentsModal({ sourceName: item.name, deployments })
                          }
                          className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold px-2.5 py-1 transition cursor-pointer"
                        >
                          <Layers size={12} />
                          {deployCount} deployment{deployCount === 1 ? "" : "s"}
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400 italic">None</span>
                      )}
                    </td>
                    <td className="py-4 px-6 text-sm">
                      <div className="flex flex-col gap-1.5 2xl:flex-row 2xl:items-center 2xl:gap-2">
                        <Button
                          onClick={() => openDeployModal(item)}
                          size="small"
                          type="secondary"
                          icon={<Rocket size={14} />}
                        >
                          Deploy
                        </Button>
                        <Button onClick={() => openUpsertModal(item)} size="small" type="primary">
                          View / Edit
                        </Button>
                        <Button onClick={() => handleDelete(item)} size="small" type="danger">
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
